/* ==========================================================================
   Lilac Haven - Collection tabs widget

   Every tab's products are already in the page: the section renders one <ul>
   per collection and hides all but the active one. So this element only
   switches which pane and which "View More" link are visible - no fetch, no
   loading state, and no flash of an empty row. That costs a few kilobytes of
   markup per extra tab, which is cheaper than a request the shopper waits for
   on a widget whose whole point is a fast browse.

   It also handles the tablist keyboard contract (arrows / Home / End move the
   selection, roving tabindex keeps one stop in the tab order) and keeps the
   selected pill inside view of the mobile scroll strip.
   ========================================================================== */

if (!customElements.get('lh-collection-tabs')) {
  class LHCollectionTabs extends HTMLElement {
    connectedCallback() {
      if (this.__wired) return;
      this.__wired = true;

      this.pills = Array.from(this.querySelectorAll('[data-lct-pill]'));
      this.panes = Array.from(this.querySelectorAll('[data-lct-pane]'));
      this.buttons = Array.from(this.querySelectorAll('[data-lct-more]'));
      if (this.pills.length === 0) return;

      this.tabs = this.querySelector('[data-lct-tabs]');

      this.pills.forEach((pill) => {
        pill.addEventListener('click', () => this.select(pill.dataset.lctPill));
        pill.addEventListener('keydown', (event) => this.onKeydown(event));
      });

      // In the theme editor, picking a tab block in the sidebar should show
      // that tab; otherwise the merchant edits a pane they cannot see.
      document.addEventListener('shopify:block:select', (event) => {
        if (!this.contains(event.target) && event.target !== this) return;
        const id = event.target.dataset && event.target.dataset.lctPane;
        if (id) this.select(id);
      });

      const active = this.pills.find((p) => p.getAttribute('aria-selected') === 'true');
      this.select(active ? active.dataset.lctPill : this.pills[0].dataset.lctPill, { focus: false, scroll: false });
    }

    select(id, options) {
      if (id == null) return;
      const opts = options || {};

      this.pills.forEach((pill) => {
        const on = pill.dataset.lctPill === id;
        pill.setAttribute('aria-selected', on ? 'true' : 'false');
        // Roving tabindex: only the selected pill is a tab stop, so Tab moves
        // past the whole strip in one press rather than through every tab.
        pill.tabIndex = on ? 0 : -1;
        if (on && opts.focus) pill.focus();
        if (on && opts.scroll !== false) this.keepInView(pill);
      });

      this.panes.forEach((pane) => {
        pane.hidden = pane.dataset.lctPane !== id;
      });

      this.buttons.forEach((btn) => {
        btn.hidden = btn.dataset.lctMore !== id;
      });
    }

    keepInView(pill) {
      if (!this.tabs) return;
      // Only worth doing while the strip actually scrolls (phones). scrollIntoView
      // with block:'nearest' would otherwise scroll the PAGE to the widget on
      // desktop, which is a jump the shopper did not ask for.
      if (this.tabs.scrollWidth <= this.tabs.clientWidth + 2) return;
      const strip = this.tabs.getBoundingClientRect();
      const box = pill.getBoundingClientRect();
      if (box.left >= strip.left && box.right <= strip.right) return;
      // scrollLeft on the strip itself rather than pill.scrollIntoView: the
      // latter is free to scroll ANY scrollable ancestor, and on a page where
      // the widget is already in view that means jumping the whole document to
      // re-centre a pill the shopper just tapped.
      const pad = 12;
      const delta = box.left < strip.left ? box.left - strip.left - pad : box.right - strip.right + pad;
      this.tabs.scrollBy({ left: delta, behavior: 'smooth' });
    }

    onKeydown(event) {
      const index = this.pills.indexOf(event.target);
      if (index === -1) return;

      let next = null;
      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          next = (index + 1) % this.pills.length;
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          next = (index - 1 + this.pills.length) % this.pills.length;
          break;
        case 'Home':
          next = 0;
          break;
        case 'End':
          next = this.pills.length - 1;
          break;
        default:
          return;
      }

      event.preventDefault();
      this.select(this.pills[next].dataset.lctPill, { focus: true });
    }
  }

  customElements.define('lh-collection-tabs', LHCollectionTabs);
}
